type Replaceable = Node & { append(...nodes: Array<Node | string>): void }

export function replaceChildrenPolyfill(this: Replaceable, ...nodes: Array<Node | string>): void {
    while (this.lastChild) this.removeChild(this.lastChild)
    this.append(...nodes)
}

function polyfillReplaceChildren(): void {
    for (const proto of [Element.prototype, Document.prototype, DocumentFragment.prototype]) {
        if ('replaceChildren' in proto) continue
        Object.defineProperty(proto, 'replaceChildren', {
            configurable: true,
            writable: true,
            value: replaceChildrenPolyfill,
        })
    }
}

type ScrollArgs = [ScrollToOptions] | [number, number]

function scrollOffsets(args: ScrollArgs): { left?: number | undefined; top?: number | undefined } {
    return typeof args[0] === 'object' ? args[0] : { left: args[0], top: args[1] }
}

export function elementScrollToPolyfill(this: Element, ...args: ScrollArgs): void {
    const { left, top } = scrollOffsets(args)
    if (left !== undefined) this.scrollLeft = left
    if (top !== undefined) this.scrollTop = top
}

export function elementScrollByPolyfill(this: Element, ...args: ScrollArgs): void {
    const { left = 0, top = 0 } = scrollOffsets(args)
    this.scrollLeft += left
    this.scrollTop += top
}

function polyfillElementScroll(): void {
    if (!('scrollTo' in Element.prototype))
        Object.defineProperty(Element.prototype, 'scrollTo', {
            configurable: true,
            writable: true,
            value: elementScrollToPolyfill,
        })
    if (!('scrollBy' in Element.prototype))
        Object.defineProperty(Element.prototype, 'scrollBy', {
            configurable: true,
            writable: true,
            value: elementScrollByPolyfill,
        })
}

class PolyfilledAbortSignal {
    aborted = false
    reason: unknown = undefined
    onabort: (() => void) | null = null
    private readonly listeners: Array<() => void> = []

    addEventListener(type: string, listener: () => void): void {
        if (type === 'abort') this.listeners.push(listener)
    }

    removeEventListener(type: string, listener: () => void): void {
        const index = this.listeners.indexOf(listener)
        if (type === 'abort' && index >= 0) this.listeners.splice(index, 1)
    }

    throwIfAborted(): void {
        if (this.aborted) throw this.reason
    }

    fireAbort(reason: unknown): void {
        if (this.aborted) return
        this.aborted = true
        this.reason = reason ?? new Error('AbortError')
        this.onabort?.()
        for (const listener of this.listeners.slice()) listener()
    }
}

class PolyfilledAbortController {
    readonly signal = new PolyfilledAbortSignal()

    abort(reason?: unknown): void {
        this.signal.fireAbort(reason)
    }
}

function polyfillAbortController(): void {
    if (typeof AbortController !== 'undefined') return
    Object.assign(window, {
        AbortController: PolyfilledAbortController,
        AbortSignal: PolyfilledAbortSignal,
    })
}

type ObserverCallback = (entries: Array<{ target: Element; contentRect: DOMRectReadOnly }>) => void

class PolyfilledResizeObserver {
    private readonly sizes = new Map<Element, string>()
    private frame = 0
    private readonly mutations = new MutationObserver(() => this.schedule())
    private readonly onWindowResize = () => this.schedule()

    constructor(private readonly callback: ObserverCallback) {}

    observe(target: Element): void {
        if (this.sizes.has(target)) return
        if (this.sizes.size === 0) {
            window.addEventListener('resize', this.onWindowResize)
            this.mutations.observe(document.documentElement, {
                attributes: true,
                childList: true,
                subtree: true,
                characterData: true,
            })
        }
        this.sizes.set(target, '')
        this.schedule()
    }

    unobserve(target: Element): void {
        this.sizes.delete(target)
        if (this.sizes.size === 0) this.disconnect()
    }

    disconnect(): void {
        this.sizes.clear()
        this.mutations.disconnect()
        window.removeEventListener('resize', this.onWindowResize)
        cancelAnimationFrame(this.frame)
        this.frame = 0
    }

    private schedule(): void {
        if (this.frame) return
        this.frame = requestAnimationFrame(() => {
            this.frame = 0
            this.notifyChanged()
        })
    }

    private notifyChanged(): void {
        const changed: Array<{ target: Element; contentRect: DOMRectReadOnly }> = []
        for (const [target, previous] of this.sizes) {
            const rect = target.getBoundingClientRect()
            const size = `${rect.width}x${rect.height}`
            if (size === previous) continue
            this.sizes.set(target, size)
            changed.push({ target, contentRect: rect })
        }
        if (changed.length) this.callback(changed)
    }
}

function polyfillResizeObserver(): void {
    if (typeof ResizeObserver !== 'undefined') return
    Object.assign(window, { ResizeObserver: PolyfilledResizeObserver })
}

type HeadersInit = Headers | Array<[string, string]> | Record<string, string>

function headersAcceptInit(): boolean {
    try {
        return new Headers({ probe: '1' }).get('probe') === '1' && !!new Headers(undefined)
    } catch {
        return false
    }
}

function polyfillHeadersInit(): void {
    if (typeof Headers === 'undefined' || headersAcceptInit()) return
    const NativeHeaders = Headers
    function InitHeaders(init?: HeadersInit): Headers {
        const headers = new NativeHeaders()
        if (init instanceof NativeHeaders)
            init.forEach((value, name) => headers.append(name, value))
        else if (Array.isArray(init)) init.forEach(([name, value]) => headers.append(name, value))
        else if (init) Object.keys(init).forEach((name) => headers.append(name, init[name]))
        return headers
    }
    InitHeaders.prototype = NativeHeaders.prototype
    Object.assign(window, { Headers: InitHeaders })
}

polyfillReplaceChildren()
polyfillElementScroll()
polyfillHeadersInit()
polyfillAbortController()
polyfillResizeObserver()
